<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ScheduledMessageApi from 'dashboard/api/inbox/scheduledMessage';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    default: null,
  },
});

const { t } = useI18n();
const isOpen = ref(false);
const scheduleMessage = ref('');
const scheduleAt = ref('');
const attachedFiles = ref([]);
const scheduledMessages = ref([]);
const isSaving = ref(false);
const isLoadingScheduled = ref(false);
const isDeletingMap = ref({});
const deleteConfirmDialogRef = ref(null);
const itemPendingDelete = ref(null);

const isRecording = ref(false);
const recordingDuration = ref(0);
const mediaRecorder = ref(null);
const mediaStream = ref(null);
const recordingTimer = ref(null);

const targetConversationId = computed(() => props.conversationId);
const canSchedule = computed(
  () =>
    (!!scheduleMessage.value || attachedFiles.value.length > 0) &&
    !!scheduleAt.value &&
    !isSaving.value
);
const pendingCount = computed(
  () => scheduledMessages.value.filter(item => item.status === 'pending').length
);

const formatDateTimeLocal = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

const presetTime = minutes => {
  const nextTime = new Date(Date.now() + minutes * 60 * 1000);
  scheduleAt.value = formatDateTimeLocal(nextTime);
};

const formatScheduleTime = value => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const statusClass = status => {
  if (status === 'pending') return 'bg-n-blue-2 text-n-blue-11';
  if (status === 'sent') return 'bg-n-teal-2 text-n-teal-11';
  if (status === 'failed') return 'bg-n-ruby-2 text-n-ruby-11';
  return 'bg-n-alpha-2 text-n-slate-11';
};

const resetForm = () => {
  scheduleMessage.value = '';
  scheduleAt.value = formatDateTimeLocal(new Date(Date.now() + 30 * 60 * 1000));
  attachedFiles.value = [];
};

const loadScheduledMessages = async () => {
  if (!targetConversationId.value) {
    scheduledMessages.value = [];
    return;
  }

  isLoadingScheduled.value = true;
  try {
    const response = await ScheduledMessageApi.list(targetConversationId.value);
    scheduledMessages.value = response.data.payload || [];
  } catch {
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGE.ERRORS.LOAD'));
  } finally {
    isLoadingScheduled.value = false;
  }
};

const onFileChange = event => {
  const files = Array.from(event.target.files || []);
  if (!files.length) return;
  attachedFiles.value = [...attachedFiles.value, ...files];
  event.target.value = null;
};

const removeAttachment = index => {
  attachedFiles.value.splice(index, 1);
};

const stopMediaTracks = () => {
  mediaStream.value?.getTracks()?.forEach(track => track.stop());
  mediaStream.value = null;
};

const stopRecordingTimer = () => {
  if (!recordingTimer.value) return;
  clearInterval(recordingTimer.value);
  recordingTimer.value = null;
};

const startAudioRecording = async () => {
  if (!navigator.mediaDevices?.getUserMedia) {
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGE.ERRORS.RECORD_NOT_SUPPORTED'));
    return;
  }

  try {
    mediaStream.value = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    const chunks = [];
    mediaRecorder.value = new MediaRecorder(mediaStream.value);
    mediaRecorder.value.ondataavailable = event => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    mediaRecorder.value.onstop = () => {
      const blob = new Blob(chunks, { type: 'audio/webm' });
      const file = new File([blob], `recording-${Date.now()}.webm`, {
        type: 'audio/webm',
      });
      attachedFiles.value = [...attachedFiles.value, file];
      stopMediaTracks();
      stopRecordingTimer();
      isRecording.value = false;
      recordingDuration.value = 0;
    };

    mediaRecorder.value.start();
    isRecording.value = true;
    recordingDuration.value = 0;
    recordingTimer.value = setInterval(() => {
      recordingDuration.value += 1;
    }, 1000);
  } catch {
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGE.ERRORS.RECORD_START'));
    stopMediaTracks();
  }
};

const stopAudioRecording = () => {
  if (!mediaRecorder.value || mediaRecorder.value.state === 'inactive') return;
  mediaRecorder.value.stop();
};

const schedule = async () => {
  if (!targetConversationId.value || !canSchedule.value) return;

  isSaving.value = true;
  try {
    await ScheduledMessageApi.create({
      conversationId: targetConversationId.value,
      message: scheduleMessage.value,
      scheduledAt: new Date(scheduleAt.value).toISOString(),
      files: attachedFiles.value,
    });
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGE.SUCCESS.CREATED'));
    resetForm();
    await loadScheduledMessages();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.SCHEDULED_MESSAGE.ERRORS.CREATE')
    );
  } finally {
    isSaving.value = false;
  }
};

const deleteScheduled = async item => {
  if (!targetConversationId.value || !item?.id) return;

  isDeletingMap.value[item.id] = true;
  try {
    await ScheduledMessageApi.destroy(targetConversationId.value, item.id);
    scheduledMessages.value = scheduledMessages.value.filter(
      row => row.id !== item.id
    );
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGE.SUCCESS.DELETED'));
  } catch {
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGE.ERRORS.DELETE'));
  } finally {
    isDeletingMap.value[item.id] = false;
  }
};

const requestDeleteScheduled = item => {
  itemPendingDelete.value = item;
  deleteConfirmDialogRef.value?.open();
};

const confirmDeleteScheduled = async () => {
  if (!itemPendingDelete.value) return;

  await deleteScheduled(itemPendingDelete.value);
  itemPendingDelete.value = null;
  deleteConfirmDialogRef.value?.close();
};

const close = () => {
  isOpen.value = false;
};

const open = async () => {
  if (!targetConversationId.value) {
    useAlert(t('CONVERSATION.SCHEDULED_MESSAGE.ERRORS.NO_CONVERSATION'));
    return;
  }

  resetForm();
  await loadScheduledMessages();
  isOpen.value = true;
};

onBeforeUnmount(() => {
  stopAudioRecording();
  stopMediaTracks();
  stopRecordingTimer();
});

watch(
  () => props.conversationId,
  () => {
    scheduledMessages.value = [];
    close();
  }
);

defineExpose({ open });
</script>

<template>
  <div>
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[1300] bg-n-alpha-black1/70 backdrop-blur-sm"
    >
      <div
        class="flex h-full w-full flex-col bg-gradient-to-br from-n-solid-1 via-n-solid-1 to-n-alpha-1"
      >
        <header
          class="sticky top-0 z-10 border-b border-n-alpha-3 bg-n-solid-1/95 px-4 py-3 backdrop-blur-sm"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <h2 class="text-xl font-semibold text-n-slate-12">
                {{ t('CONVERSATION.SCHEDULED_MESSAGE.TITLE') }}
              </h2>
              <p class="text-sm text-n-slate-11">
                {{ t('CONVERSATION.SCHEDULED_MESSAGE.DESCRIPTION') }}
              </p>
            </div>
            <button
              type="button"
              class="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-n-alpha-3 text-n-slate-11 transition hover:border-n-alpha-5 hover:text-n-slate-12"
              :aria-label="t('CONVERSATION.HEADER.CLOSE')"
              @click="close"
            >
              <span class="i-lucide-x text-lg" />
            </button>
          </div>
        </header>

        <div
          class="grid flex-1 grid-cols-1 gap-4 overflow-hidden p-4 md:grid-cols-12"
        >
          <section class="space-y-4 md:col-span-7 md:overflow-y-auto md:pr-2">
            <div class="rounded-2xl border border-n-alpha-3 bg-n-solid-1 p-4">
              <p
                class="mb-3 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
              >
                {{ t('CONVERSATION.SCHEDULED_MESSAGE.FIELDS.MESSAGE') }}
              </p>
              <Input
                v-model="scheduleMessage"
                :placeholder="
                  t('CONVERSATION.SCHEDULED_MESSAGE.FIELDS.MESSAGE_PLACEHOLDER')
                "
                custom-input-class="!mb-0 min-h-[130px]"
              />
            </div>

            <div class="rounded-2xl border border-n-alpha-3 bg-n-solid-1 p-4">
              <p
                class="mb-3 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
              >
                {{ t('CONVERSATION.SCHEDULED_MESSAGE.FIELDS.SCHEDULE_AT') }}
              </p>
              <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                <input
                  v-model="scheduleAt"
                  type="datetime-local"
                  class="w-full rounded-xl border border-n-alpha-3 bg-n-background px-3 py-2.5 text-sm text-n-slate-12"
                />
                <div class="flex flex-wrap gap-2">
                  <Button
                    size="xs"
                    color="slate"
                    variant="outline"
                    :label="t('CONVERSATION.SCHEDULED_MESSAGE.ACTIONS.IN_15')"
                    @click="presetTime(15)"
                  />
                  <Button
                    size="xs"
                    color="slate"
                    variant="outline"
                    :label="t('CONVERSATION.SCHEDULED_MESSAGE.ACTIONS.IN_30')"
                    @click="presetTime(30)"
                  />
                  <Button
                    size="xs"
                    color="slate"
                    variant="outline"
                    :label="t('CONVERSATION.SCHEDULED_MESSAGE.ACTIONS.IN_60')"
                    @click="presetTime(60)"
                  />
                </div>
              </div>
            </div>

            <div class="rounded-2xl border border-n-alpha-3 bg-n-solid-1 p-4">
              <p
                class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
              >
                {{ t('CONVERSATION.SCHEDULED_MESSAGE.FIELDS.ATTACHMENTS') }}
              </p>
              <div class="mb-3 flex flex-wrap gap-2">
                <span
                  class="rounded-full bg-n-blue-2 px-2 py-0.5 text-xs text-n-blue-11"
                >
                  {{ t('CONVERSATION.SCHEDULED_MESSAGE.FIELDS.TYPE_IMAGE') }}
                </span>
                <span
                  class="rounded-full bg-n-teal-2 px-2 py-0.5 text-xs text-n-teal-11"
                >
                  {{ t('CONVERSATION.SCHEDULED_MESSAGE.FIELDS.TYPE_VIDEO') }}
                </span>
                <span
                  class="rounded-full bg-n-amber-2 px-2 py-0.5 text-xs text-n-amber-11"
                >
                  {{ t('CONVERSATION.SCHEDULED_MESSAGE.FIELDS.TYPE_AUDIO') }}
                </span>
                <span
                  class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
                >
                  {{ t('CONVERSATION.SCHEDULED_MESSAGE.FIELDS.TYPE_FILE') }}
                </span>
              </div>

              <div class="flex flex-wrap items-center gap-2">
                <label
                  class="inline-flex cursor-pointer items-center rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-xs font-medium text-n-slate-12 hover:border-n-blue-7/40"
                >
                  {{ t('CONVERSATION.SCHEDULED_MESSAGE.ACTIONS.ADD_FILES') }}
                  <input
                    type="file"
                    multiple
                    class="hidden"
                    @change="onFileChange"
                  />
                </label>
                <Button
                  size="xs"
                  color="teal"
                  variant="outline"
                  icon="i-lucide-mic"
                  :label="
                    isRecording
                      ? `${t('CONVERSATION.SCHEDULED_MESSAGE.ACTIONS.STOP_RECORDING')} (${recordingDuration}s)`
                      : t('CONVERSATION.SCHEDULED_MESSAGE.ACTIONS.RECORD_AUDIO')
                  "
                  @click="
                    isRecording ? stopAudioRecording() : startAudioRecording()
                  "
                />
              </div>

              <div
                v-if="attachedFiles.length"
                class="mt-3 max-h-56 space-y-2 overflow-y-auto pr-1"
              >
                <div
                  v-for="(file, index) in attachedFiles"
                  :key="`${file.name}-${index}`"
                  class="flex items-center justify-between rounded-xl border border-n-alpha-3 bg-n-background px-3 py-2"
                >
                  <div class="min-w-0">
                    <p class="truncate text-xs font-medium text-n-slate-12">
                      {{ file.name }}
                    </p>
                    <p class="text-[11px] text-n-slate-10">
                      {{ file.type || '-' }}
                    </p>
                  </div>
                  <Button
                    size="xs"
                    color="ruby"
                    variant="faded"
                    :label="
                      t('CONVERSATION.SCHEDULED_MESSAGE.ACTIONS.REMOVE_FILE')
                    "
                    @click="removeAttachment(index)"
                  />
                </div>
              </div>
            </div>
          </section>

          <aside class="md:col-span-5 md:overflow-y-auto">
            <div class="rounded-2xl border border-n-alpha-3 bg-n-solid-1 p-4">
              <div class="mb-3 flex items-center justify-between">
                <div>
                  <p class="text-sm font-semibold text-n-slate-12">
                    {{ t('CONVERSATION.SCHEDULED_MESSAGE.PENDING.TITLE') }}
                  </p>
                  <p class="text-xs text-n-slate-10">
                    {{
                      t('CONVERSATION.SCHEDULED_MESSAGE.PENDING.COUNT', {
                        count: pendingCount,
                      })
                    }}
                  </p>
                </div>
                <Button
                  size="xs"
                  color="slate"
                  variant="outline"
                  :label="t('CONVERSATION.SCHEDULED_MESSAGE.ACTIONS.REFRESH')"
                  :is-loading="isLoadingScheduled"
                  @click="loadScheduledMessages"
                />
              </div>

              <div
                v-if="isLoadingScheduled"
                class="py-6 text-center text-sm text-n-slate-11"
              >
                {{ t('CONVERSATION.SCHEDULED_MESSAGE.PENDING.LOADING') }}
              </div>
              <div
                v-else-if="!scheduledMessages.length"
                class="py-6 text-center text-sm text-n-slate-11"
              >
                {{ t('CONVERSATION.SCHEDULED_MESSAGE.PENDING.EMPTY') }}
              </div>
              <div v-else class="max-h-[58vh] space-y-2 overflow-y-auto pr-1">
                <div
                  v-for="item in scheduledMessages"
                  :key="item.id"
                  class="rounded-xl border border-n-alpha-3 bg-n-background p-3"
                >
                  <div class="mb-2 flex items-center justify-between gap-2">
                    <span
                      class="rounded-full px-2 py-0.5 text-[11px] font-medium"
                      :class="statusClass(item.status)"
                    >
                      {{ item.status }}
                    </span>
                    <Button
                      size="xs"
                      color="ruby"
                      variant="outline"
                      :label="
                        t('CONVERSATION.SCHEDULED_MESSAGE.ACTIONS.DELETE')
                      "
                      :is-loading="isDeletingMap[item.id]"
                      @click="requestDeleteScheduled(item)"
                    />
                  </div>
                  <p class="truncate text-xs font-medium text-n-slate-12">
                    {{
                      item.content ||
                      t(
                        'CONVERSATION.SCHEDULED_MESSAGE.PENDING.ATTACHMENT_ONLY'
                      )
                    }}
                  </p>
                  <p class="mt-1 text-[11px] text-n-slate-10">
                    {{ formatScheduleTime(item.scheduled_at) }}
                  </p>
                  <p
                    v-if="item.error_message"
                    class="mt-1 text-[11px] text-n-ruby-11"
                  >
                    {{ item.error_message }}
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <footer
          class="sticky bottom-0 z-10 border-t border-n-alpha-3 bg-n-solid-1/95 px-4 py-3 backdrop-blur-sm"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <p class="text-xs text-n-slate-10">
              {{
                t('CONVERSATION.SCHEDULED_MESSAGE.FOOTER_HINT', {
                  count: attachedFiles.length,
                })
              }}
            </p>
            <div class="flex gap-2">
              <Button
                size="sm"
                color="slate"
                variant="outline"
                :label="t('CONVERSATION.HEADER.CLOSE')"
                @click="close"
              />
              <Button
                size="sm"
                color="blue"
                icon="i-lucide-calendar-check-2"
                :label="t('CONVERSATION.SCHEDULED_MESSAGE.ACTIONS.SCHEDULE')"
                :is-loading="isSaving"
                :disabled="!canSchedule"
                @click="schedule"
              />
            </div>
          </div>
        </footer>
      </div>
    </div>
    <Dialog
      ref="deleteConfirmDialogRef"
      type="alert"
      :title="t('CONVERSATION.SCHEDULED_MESSAGE.DELETE_CONFIRM.TITLE')"
      :description="
        t('CONVERSATION.SCHEDULED_MESSAGE.DELETE_CONFIRM.DESCRIPTION')
      "
      :confirm-button-label="
        t('CONVERSATION.SCHEDULED_MESSAGE.DELETE_CONFIRM.CONFIRM')
      "
      :is-loading="isDeletingMap[itemPendingDelete?.id]"
      @confirm="confirmDeleteScheduled"
    />
  </div>
</template>
